/**
 * Canonical widget settings schema — defaults, option lists, validation, and the
 * map from what we store to what the widget reads.
 *
 * THIS FILE IS THE ONLY PLACE ANY OF THAT IS DEFINED.
 *
 * Three surfaces consume it:
 *   - src/pages/AppearancePage.jsx   builds its controls from ENUMS / FONT_OPTIONS
 *   - src/widget/embed.ts + preview  call toWidgetSettings() before mounting
 *   - backend/shared/widgetSettings.js  (GENERATED — see scripts/sync-widget-settings.js)
 *     drives the save validator and the public config endpoint
 *
 * The backend deploys as its own package and cannot import across src/, so the
 * build copies this file rather than sharing it. Never hand-edit the copy.
 *
 * Two long-standing bugs are structurally prevented by keeping it in one place:
 *
 *   1. Validator drift. The dashboard used to offer options the backend
 *      validator rejected with a 400 (animationStyle "bounce", borderRadius
 *      above 24). Both now read the same ENUMS.
 *   2. Fields that save but never render. publicWidgetController used to
 *      rebuild the appearance object field by field, so anything not listed
 *      there silently never reached the widget. It now serves toWidgetSettings()
 *      output, so a field that saves is a field that renders.
 *
 * Plain CommonJS on purpose: it has to be readable by webpack, esbuild and node
 * without a build step in front of it.
 */

/* ------------------------------------------------------------------ *
 * Option lists — the single source for every enumerable control
 * ------------------------------------------------------------------ */

const ENUMS = {
  theme: ['light', 'dark'],
  position: ['bottom-right', 'bottom-left', 'top-right', 'top-left'],
  size: ['small', 'medium', 'large'],
  headerStyle: ['solid', 'gradient'],
  messageSpacing: ['compact', 'normal', 'spacious'],
  buttonIcon: ['chat-circle', 'note', 'chat-square', 'paper-plane', 'bulb', 'planet', 'leaf'],
  logoShape: ['circle', 'rounded', 'square'],
  logoFit: ['cover', 'contain'],
  typingIndicator: ['dots', 'wave', 'pulse'],
  defaultView: ['home', 'chat'],
};

/** Discrete padding steps, px. Kept a closed set so the control stays a
 *  segmented picker rather than a free number the widget has to sanitise. */
const LOGO_PADDING_STEPS = [0, 4, 8, 12];

/** Panel radius. The widget caps at 28 in widgetCss(); the slider must not
 *  offer more than the widget will honour or the preview lies. */
const BORDER_RADIUS = { min: 0, max: 28, default: 18 };

/** Google faces, then the six that ship with the OS. The widget skips the
 *  Google Fonts request for the system ones — see SYSTEM_FONTS in widget-ui.ts.
 *
 *  This is the UNION of the faces the Wix widget shipped with and the 40 the
 *  old dashboard picker offered. The port brought only the Wix set, which
 *  dropped 23 faces (Manrope, DM Sans, Nunito, Outfit, …) that live rows were
 *  already storing — those rows then failed validation on save with "that font
 *  is not in the supported list" for a font the widget renders perfectly well.
 *  ensureFont() fetches any family by name, so a longer list costs nothing;
 *  removing a name from it silently invalidates whoever picked it. */
const FONT_OPTIONS = [
  'Inter', 'Alegreya', 'Alegreya Sans', 'Anonymous Pro', 'Archivo',
  'Archivo Narrow', 'Barlow', 'BioRhyme', 'Cabin', 'Cardo', 'Chivo',
  'Cormorant', 'Crimson Text', 'DM Sans', 'Eczar', 'Figtree', 'Fira Sans',
  'Geist', 'IBM Plex Sans', 'Inconsolata', 'Inknut Antiqua', 'Josefin Sans',
  'Karla', 'Lato', 'Lexend', 'Libre Baskerville', 'Libre Franklin', 'Lora',
  'Manrope', 'Merriweather', 'Montserrat', 'Mulish', 'Neuton', 'Noto Sans',
  'Nunito', 'Onest', 'Open Sans', 'Oswald', 'Outfit', 'PT Sans', 'PT Serif',
  'Playfair Display', 'Plus Jakarta Sans', 'Poppins', 'Proza Libre',
  'Quicksand', 'Raleway', 'Red Hat Display', 'Roboto', 'Roboto Slab', 'Rubik',
  'Sora', 'Source Sans 3', 'Source Sans Pro', 'Source Serif Pro',
  'Space Grotesk', 'Space Mono', 'Spectral', 'Syne', 'Titillium Web',
  'Ubuntu', 'Work Sans',
  'Arial', 'Helvetica', 'Georgia', 'Times New Roman', 'Verdana', 'Courier New',
];

/** Panel dimensions per size preset. Must stay identical to SIZES in
 *  widget-ui.ts — the settings cards advertise these numbers, and they have
 *  drifted from the real panel once already. */
const SIZE_DIMENSIONS = {
  small: [360, 560],
  medium: [400, 640],
  large: [440, 720],
};

/* ------------------------------------------------------------------ *
 * Defaults
 * ------------------------------------------------------------------ */

/**
 * Stored shape (camelCase), matching the `appearance` JSONB column.
 *
 * These defaults are deliberately not auto-generated-looking. A fresh install
 * previously read "Welcome! I'm your AI assistant" over three invented starter
 * questions, which made every customer's widget look like a demo of an AI
 * product rather than that business's own support channel. The assistant is
 * called Support, the greeting sounds like a person wrote it, and starters ship
 * EMPTY — an empty Home list is honest, invented questions are not.
 */
const DEFAULT_APPEARANCE = {
  // Style
  theme: 'light',
  primaryColor: '#2E5BFF',
  headerStyle: 'solid',
  gradientColor: '',
  position: 'bottom-right',
  size: 'medium',
  borderRadius: BORDER_RADIUS.default,
  buttonBgColor: '#2E5BFF',
  buttonIconColor: '#FFFFFF',
  buttonIcon: 'chat-circle',
  fontFamily: 'Inter',
  messageSpacing: 'normal',

  // Branding
  botName: 'Support',
  companyLogo: '',
  showCompanyLogo: true,
  useLogoAsButtonIcon: false,
  logoShape: 'circle',
  logoFit: 'cover',
  logoPadding: 0,
  hideBranding: false,

  // Content
  welcomeTitle: 'Hi there! 👋',
  greeting: 'Ask us anything — we usually reply in a few seconds.',
  placeholder: 'Type your message…',
  showSuggestions: true,
  customSuggestions: [],
  typingIndicator: 'dots',

  // Home screen & channels
  showHomeTab: true,
  showWhatsapp: false,
  whatsappNumber: '',
  showEmail: false,
  emailAddress: '',
  showPhone: false,
  phoneNumber: '',

  // Support contact — surfaced in the widget when the assistant is rate-limited
  supportEmail: '',
  supportPhone: '',

  // Behaviour
  autoOpen: false,
  // Which view the panel lands on when it opens. Ignored when the home tab is
  // off, since chat is then the only view there is.
  defaultView: 'home',
};

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

function pickEnum(value, allowed, fallback) {
  return allowed.indexOf(value) !== -1 ? value : fallback;
}

function bool(value, fallback) {
  return typeof value === 'boolean' ? value : fallback;
}

function str(value, fallback) {
  return typeof value === 'string' && value.trim() !== '' ? value : fallback;
}

/**
 * Absolutise a stored logo URL.
 *
 * Uploads write a RELATIVE path ("/api/appearance/logo/{id}"). That resolves
 * fine in the dashboard, which shares an origin with the API — but the embed
 * runs on the customer's own domain, where the same path 404s against their
 * server. Every logo slot has to go through here.
 */
function resolveLogoUrl(url, apiBase) {
  if (!url) return '';
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  if (!apiBase) return url;
  return apiBase.replace(/\/$/, '') + (url.charAt(0) === '/' ? '' : '/') + url;
}

function digitsOnly(value) {
  return String(value || '').replace(/[^\d]/g, '');
}

/* ------------------------------------------------------------------ *
 * Legacy rows
 * ------------------------------------------------------------------ */

/**
 * Lift retired field names onto their current equivalents.
 *
 * Rows written before this schema carry `companyName` for what is now
 * `botName`, and a `gradientEnabled` boolean for what is now `headerStyle`.
 * This has to run BEFORE anything that iterates the current key set —
 * otherwise the old key is dropped as unknown and the customer silently loses
 * the setting: the assistant name resets to "Support" and a gradient header
 * flips back to solid the first time they save anything at all.
 *
 * Only fills gaps, so it can run repeatedly and never overwrites a real value.
 *
 * @param {object} stored raw `appearance` JSONB
 * @returns {object} a copy with legacy names mapped forward
 */
function migrateLegacyAppearance(stored) {
  const a = Object.assign({}, stored || {});

  if (a.botName === undefined && typeof a.companyName === 'string' && a.companyName !== '') {
    a.botName = a.companyName;
  }
  if (a.headerStyle === undefined && a.gradientEnabled !== undefined) {
    a.headerStyle = a.gradientEnabled === true ? 'gradient' : 'solid';
  }

  return a;
}

/* ------------------------------------------------------------------ *
 * Stored appearance -> WidgetSettings
 * ------------------------------------------------------------------ */

/**
 * Map the stored camelCase appearance object onto the snake_case shape
 * widget-ui.ts reads, filling every gap from DEFAULT_APPEARANCE.
 *
 * Keeping the stored names as they are avoids migrating live JSONB rows; the
 * rename happens here, once, instead of in each host.
 *
 * @param {object} appearance  raw `appearance` JSONB (may be null / partial)
 * @param {{apiBase?: string}} [options]  apiBase absolutises a relative logo URL
 * @returns {object} WidgetSettings
 */
function toWidgetSettings(appearance, options) {
  // Legacy names are mapped forward first so a row that has never been re-saved
  // renders with the settings its owner actually chose.
  const a = migrateLegacyAppearance(appearance);
  const d = DEFAULT_APPEARANCE;
  const apiBase = (options && options.apiBase) || '';

  const headerStyle = pickEnum(a.headerStyle, ENUMS.headerStyle, d.headerStyle);
  const botName = str(a.botName, d.botName);

  const radius = Number(a.borderRadius);
  const borderRadius = Number.isFinite(radius)
    ? Math.min(Math.max(radius, BORDER_RADIUS.min), BORDER_RADIUS.max)
    : d.borderRadius;

  const padding = Number(a.logoPadding);
  const logoPadding = LOGO_PADDING_STEPS.indexOf(padding) !== -1 ? padding : d.logoPadding;

  return {
    // Style
    theme: pickEnum(a.theme, ENUMS.theme, d.theme),
    primary_color: str(a.primaryColor, d.primaryColor),
    header_style: headerStyle,
    gradient_color: str(a.gradientColor, d.gradientColor),
    position: pickEnum(a.position, ENUMS.position, d.position),
    widget_size: pickEnum(a.size, ENUMS.size, d.size),
    border_radius: borderRadius,
    button_color: str(a.buttonBgColor, str(a.primaryColor, d.buttonBgColor)),
    button_icon_color: str(a.buttonIconColor, d.buttonIconColor),
    button_icon: pickEnum(a.buttonIcon, ENUMS.buttonIcon, d.buttonIcon),
    font_family: str(a.fontFamily, d.fontFamily),
    message_spacing: pickEnum(a.messageSpacing, ENUMS.messageSpacing, d.messageSpacing),

    // Branding
    bot_name: botName,
    logo_url: resolveLogoUrl(str(a.companyLogo, d.companyLogo), apiBase),
    show_logo_in_header: bool(a.showCompanyLogo, d.showCompanyLogo),
    use_logo_as_button: bool(a.useLogoAsButtonIcon, d.useLogoAsButtonIcon),
    logo_shape: pickEnum(a.logoShape, ENUMS.logoShape, d.logoShape),
    logo_fit: pickEnum(a.logoFit, ENUMS.logoFit, d.logoFit),
    logo_padding: logoPadding,
    hide_branding: bool(a.hideBranding, d.hideBranding),

    // Content
    welcome_title: str(a.welcomeTitle, d.welcomeTitle),
    welcome_message: str(a.greeting, d.greeting),
    placeholder_text: str(a.placeholder, d.placeholder),
    show_starters: bool(a.showSuggestions, d.showSuggestions),
    starters: Array.isArray(a.customSuggestions)
      ? a.customSuggestions.filter(function (x) {
          return typeof x === 'string' && x.trim() !== '';
        })
      : d.customSuggestions.slice(),
    typing_indicator: pickEnum(a.typingIndicator, ENUMS.typingIndicator, d.typingIndicator),

    // Home screen & channels. A channel reaches the widget only when its toggle
    // is on AND the value is filled — a half-configured channel would otherwise
    // render a row linking to "tel:" with nothing after it.
    show_home_tab: bool(a.showHomeTab, d.showHomeTab),
    show_whatsapp: bool(a.showWhatsapp, d.showWhatsapp) && digitsOnly(a.whatsappNumber) !== '',
    whatsapp_number: str(a.whatsappNumber, d.whatsappNumber),
    show_email: bool(a.showEmail, d.showEmail) && str(a.emailAddress, '') !== '',
    email_address: str(a.emailAddress, d.emailAddress),
    show_phone: bool(a.showPhone, d.showPhone) && digitsOnly(a.phoneNumber) !== '',
    phone_number: str(a.phoneNumber, d.phoneNumber),

    // Support contact
    support_email: str(a.supportEmail, d.supportEmail),
    support_phone: str(a.supportPhone, d.supportPhone),

    // Behaviour
    auto_open: bool(a.autoOpen, d.autoOpen),
    default_view: pickEnum(a.defaultView, ENUMS.defaultView, d.defaultView),
  };
}

/* ------------------------------------------------------------------ *
 * Validation — shared by the form's inline errors and the save endpoint
 * ------------------------------------------------------------------ */

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate a stored-shape appearance object.
 *
 * One implementation so the form and the endpoint can't disagree about what is
 * saveable. Returns a field -> message map; empty means valid.
 *
 * @param {object} appearance
 * @returns {Record<string, string>}
 */
function validateAppearance(appearance) {
  const a = appearance || {};
  const errors = {};

  [
    ['primaryColor', 'Primary color'],
    ['buttonBgColor', 'Button color'],
    ['buttonIconColor', 'Button icon color'],
  ].forEach(function (pair) {
    const value = a[pair[0]];
    if (value !== undefined && value !== '' && !HEX.test(String(value))) {
      errors[pair[0]] = pair[1] + ' must be a hex color like #2E5BFF.';
    }
  });

  // Empty is meaningful here: it tells the widget to derive the second stop.
  if (a.gradientColor !== undefined && a.gradientColor !== '' && !HEX.test(String(a.gradientColor))) {
    errors.gradientColor = 'Gradient color must be a hex color like #1B3AB8.';
  }

  // Every enumerable field checked against the same list the form renders from,
  // which is what stops the dashboard offering an option the save rejects.
  Object.keys(ENUMS).forEach(function (key) {
    const value = a[key];
    if (value !== undefined && ENUMS[key].indexOf(value) === -1) {
      errors[key] = 'Choose one of: ' + ENUMS[key].join(', ') + '.';
    }
  });

  if (a.borderRadius !== undefined) {
    const r = Number(a.borderRadius);
    if (!Number.isFinite(r) || r < BORDER_RADIUS.min || r > BORDER_RADIUS.max) {
      errors.borderRadius =
        'Corner radius must be between ' + BORDER_RADIUS.min + ' and ' + BORDER_RADIUS.max + '.';
    }
  }

  if (a.logoPadding !== undefined && LOGO_PADDING_STEPS.indexOf(Number(a.logoPadding)) === -1) {
    errors.logoPadding = 'Logo padding must be one of: ' + LOGO_PADDING_STEPS.join(', ') + '.';
  }

  if (a.fontFamily !== undefined && FONT_OPTIONS.indexOf(a.fontFamily) === -1) {
    errors.fontFamily = 'That font is not in the supported list.';
  }

  if (a.botName !== undefined && String(a.botName).length > 40) {
    errors.botName = 'Assistant name must be 40 characters or fewer.';
  }

  if (Array.isArray(a.customSuggestions)) {
    if (a.customSuggestions.length > 6) {
      errors.customSuggestions = 'Up to 6 conversation starters.';
    } else if (a.customSuggestions.some(function (x) { return String(x).length > 80; })) {
      errors.customSuggestions = 'Each starter must be 80 characters or fewer.';
    }
  }

  // A toggled-on channel with no value would render a dead link on the
  // customer's site, so it blocks the save rather than being quietly dropped.
  if (a.showWhatsapp === true && digitsOnly(a.whatsappNumber) === '') {
    errors.whatsappNumber = 'Add a WhatsApp number or turn the channel off.';
  }
  if (a.showEmail === true && !EMAIL.test(String(a.emailAddress || ''))) {
    errors.emailAddress = 'Add a valid email address or turn the channel off.';
  }
  if (a.showPhone === true && digitsOnly(a.phoneNumber) === '') {
    errors.phoneNumber = 'Add a phone number or turn the channel off.';
  }

  if (a.supportEmail !== undefined && a.supportEmail !== '' && !EMAIL.test(String(a.supportEmail))) {
    errors.supportEmail = 'Support email must be a valid address.';
  }

  return errors;
}

/* ------------------------------------------------------------------ *
 * Sanitising a stored row
 * ------------------------------------------------------------------ */

/**
 * Coerce a stored appearance object into one the form can render and
 * validateAppearance() accepts, dropping keys we no longer keep.
 *
 * This exists because the renderer and the validator used to disagree.
 * toWidgetSettings() has always coerced — pickEnum falls back, borderRadius
 * clamps — so a row holding a retired value still *rendered* fine. But the
 * dashboard loaded that same raw value into the form and then validated it on
 * save, so the widget looked correct while "Save" refused with an error about a
 * field the merchant had never touched. Two real cases from the Aug 2026 port:
 * a borderRadius of 32 (the old slider's max; the new one and the widget both
 * cap at 28) and any of the 23 faces the font list lost.
 *
 * Post-condition: validateAppearance(sanitizeAppearance(x)) is always {}.
 * Run it on read and on save so a stale value can never reach the form.
 *
 * @param {object} appearance raw `appearance` JSONB (may be null / partial / legacy)
 * @returns {object} stored-shape object, every key valid, no unknown keys
 */
function sanitizeAppearance(appearance) {
  // Legacy names first — the walk below only visits current keys, so an
  // unmigrated one would be dropped and the setting lost.
  const a = migrateLegacyAppearance(appearance);
  const d = DEFAULT_APPEARANCE;
  const out = {};

  // Start from the stored value, defaulting per key and keeping types honest.
  Object.keys(d).forEach(function (key) {
    const value = a[key] !== undefined ? a[key] : d[key];
    if (typeof d[key] === 'number') {
      const n = Number(value);
      out[key] = Number.isFinite(n) ? n : d[key];
    } else if (typeof d[key] === 'boolean') {
      out[key] = typeof value === 'boolean' ? value : d[key];
    } else if (Array.isArray(d[key])) {
      out[key] = Array.isArray(value) ? value.slice() : d[key].slice();
    } else {
      out[key] = typeof value === 'string' ? value : d[key];
    }
  });

  // Colors. An empty gradientColor is meaningful — it tells the widget to
  // derive the second stop — so only a malformed one is replaced.
  ['primaryColor', 'buttonBgColor', 'buttonIconColor'].forEach(function (key) {
    if (!HEX.test(String(out[key]))) out[key] = d[key];
  });
  if (out.gradientColor !== '' && !HEX.test(String(out.gradientColor))) {
    out.gradientColor = d.gradientColor;
  }

  Object.keys(ENUMS).forEach(function (key) {
    out[key] = pickEnum(out[key], ENUMS[key], d[key]);
  });

  // Clamp rather than reset: widgetCss() already caps at BORDER_RADIUS.max, so
  // a stored 32 has been rendering as 28 all along. Clamping matches what the
  // merchant already sees; resetting to the default would not.
  out.borderRadius = Math.min(Math.max(out.borderRadius, BORDER_RADIUS.min), BORDER_RADIUS.max);

  if (LOGO_PADDING_STEPS.indexOf(out.logoPadding) === -1) out.logoPadding = d.logoPadding;
  if (FONT_OPTIONS.indexOf(out.fontFamily) === -1) out.fontFamily = d.fontFamily;

  out.botName = str(out.botName, d.botName).slice(0, 40);

  out.customSuggestions = out.customSuggestions
    .filter(function (x) { return typeof x === 'string' && x.trim() !== ''; })
    .slice(0, 6)
    .map(function (x) { return x.slice(0, 80); });

  // A channel toggled on with nothing to dial would render a dead link, which
  // is why validateAppearance blocks it. Here the pair came off a stored row
  // rather than out of the form, so switch the channel off instead of leaving
  // the merchant unable to save a page they never edited. The value they typed
  // is kept, so flipping the toggle back on picks up where they left off.
  if (out.showWhatsapp && digitsOnly(out.whatsappNumber) === '') out.showWhatsapp = false;
  if (out.showEmail && !EMAIL.test(String(out.emailAddress))) out.showEmail = false;
  if (out.showPhone && digitsOnly(out.phoneNumber) === '') out.showPhone = false;

  if (out.supportEmail !== '' && !EMAIL.test(String(out.supportEmail))) {
    out.supportEmail = d.supportEmail;
  }

  return out;
}

module.exports = {
  ENUMS,
  LOGO_PADDING_STEPS,
  BORDER_RADIUS,
  FONT_OPTIONS,
  SIZE_DIMENSIONS,
  DEFAULT_APPEARANCE,
  toWidgetSettings,
  migrateLegacyAppearance,
  validateAppearance,
  sanitizeAppearance,
  resolveLogoUrl,
  digitsOnly,
};
